import { useEffect, useState } from "react";

import clsx from "clsx";

import { languageSelector } from "@/src/contexts";
import Balloon from "@Components/Balloon";
import { requestTrack } from "@Components/Modules/Helpers";
import * as stylex from "@stylexjs/stylex";

import type { IShare, ShareAppearance } from "./ShareDefinitions";
import { EmailIcon } from "./links/EmailIcon";
import { FacebookIcon } from "./links/FacebookIcon";
import ShareLink from "./links/ShareLink";
import { TwitterIcon } from "./links/TwitterIcon";

import * as css from "./Share.module.css";

const getShareAppearanceClassName = (appearance?: ShareAppearance) => (appearance === "dark" ? css.dark : "");

const Share = (props: IShare) => {
    const [showBalloon, setShowBalloon] = useState(false);
    const tooltipText = languageSelector("Udostępnij", "Share");
    const [mouseLeaveTimeout, setMouseLeaveTimeout] = useState<number | undefined>(undefined);
    const onMouseLeave = () => {
        if (!mouseLeaveTimeout) {
            setMouseLeaveTimeout(window.setTimeout(setShowBalloon, 500, false));
        }
    };

    const onMouseEnter = () => {
        if (mouseLeaveTimeout) {
            clearTimeout(mouseLeaveTimeout);
            setMouseLeaveTimeout(undefined);
        }
    };

    const showButtons = () => {
        requestTrack(props.onClickAjaxAction);

        const navShare = window.navigator;
        if (navShare && navShare.share) {
            navShare.share({
                title: props.title || document.title,
                text: props.text,
                url: props.shareUrl,
            });
        } else if (!props.disabled) {
            setShowBalloon(!showBalloon);
            onMouseEnter();
        }
    };

    useEffect(() => () => clearTimeout(mouseLeaveTimeout), [mouseLeaveTimeout]);

    const variant = props.appearance !== "default" ? "white" : "darkBlue";

    const shareUrl = encodeURIComponent(props.shareUrl).replace("localhost", "goodie.pl");

    const fbShareClick: React.MouseEventHandler<HTMLAnchorElement> = (ev) => {
        const url = `https://facebook.com/sharer.php?display=popup&u=${shareUrl}`;
        const options = "toolbar=0,status=0,resizable=1,width=626,height=436";
        window.open(url, "sharer", options);
        ev.preventDefault();
    };

    const buttons = (
        <div {...stylex.props(styles.iconsWrapper)}>
            <a
                {...stylex.props(styles.icon)}
                target="_self"
                href={`https://facebook.com/sharer.php?display=page&u=${shareUrl}&redirect_uri=${shareUrl}`}
                rel="noreferrer nofollow"
                aria-label="Facebook"
                onClick={fbShareClick}
            >
                <FacebookIcon variant={variant} />
            </a>
            <a
                {...stylex.props(styles.icon)}
                target="_blank"
                href={`https://x.com/intent/tweet?url=${shareUrl}&text=${encodeURIComponent(props.twitterText || props.title)}`}
                rel="noreferrer nofollow"
                aria-label="X"
            >
                <TwitterIcon variant={variant} />
            </a>
            <a
                {...stylex.props(styles.icon)}
                target="_blank"
                href={`mailto:?subject=${encodeURIComponent(props.title)}&body=${encodeURIComponent(`${props.text} ${props.shareUrl}`)}`}
                rel="noreferrer nofollow"
                aria-label="Email"
            >
                <EmailIcon variant={variant} />
            </a>
        </div>
    );

    const ssClass = clsx(css.shareComponent, getShareAppearanceClassName(props.appearance), props.className);

    if (props.displayInline) {
        return (
            <div className={ssClass} onMouseLeave={onMouseLeave}>
                {buttons}
            </div>
        );
    }

    return (
        <div className={ssClass} onMouseLeave={onMouseLeave}>
            <ShareLink
                onClick={showButtons}
                title={props.showTooltip && tooltipText}
                styleX={props.buttonStyleX}
                iconStyleX={props.iconStyleX}
            >
                {props.children}
            </ShareLink>
            <Balloon
                isOpen={showBalloon}
                tailPosition={props.tailPosition || "TopRight"}
                onMouseEnter={onMouseEnter}
            >
                {buttons}
            </Balloon>
        </div>
    );
};

const styles = stylex.create({
    iconsWrapper: {
        alignItems: "center",
        columnGap: 20,
        display: "flex",
        justifyContent: "end",
        lineHeight: 0,
        rowGap: 20,
        paddingBottom: 0,
        paddingLeft: 10,
        paddingRight: 10,
        paddingTop: 0,
    },
    icon: {
        verticalAlign: "middle",
        height: 22,
        width: 22,
    },
});

export default Share;
